"use strict";
/**
 * Threshold date for the Texas §50(a)(6) closing-window question.
 *
 * Shared by Partner Flow (`Texas50a6QuestionsStep.tsx`) and origination
 * (`useTexas50a6Attestation.ts`) — both apps depend on `aven_types`, so this is the single
 * definition rather than a per-app copy. The wire shape lives in `aven_contracts`
 * (`SubmitTexas50a6AttestationPayload`) and storage in the backend's `texas50a6Attestation` entity;
 * this module only computes the date the question is asked against, which the screen renders into
 * the question text. The backend recomputes its own threshold at submission time and validates
 * against that, so a client-side disagreement here can only affect what's displayed, not what's
 * persisted.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTexas50a6ThresholdDate = void 0;
/** §50(a)(6)(M)(i): a 50(a)(6) loan cannot close until 12 days after the application. */
const APPLICATION_TO_CLOSING_DAYS = 12;
/**
 * The earliest closing date for the other 50(a)(6) loan that would still block this one.
 *
 * §50(a)(6)(M)(iii) bars closing until one calendar year has passed from the other loan's closing,
 * and this transaction cannot close before `application + 12 days`. So the other loan is a problem
 * exactly when its one-year mark falls after that date — which makes the boundary a calendar
 * operation, not a day count:
 *
 *     threshold = (application + 12 days) − 1 year + 1 day
 *
 * The `+ 1 day` keeps a legal closing from being flagged: a loan that reaches its one-year mark on
 * the earliest legal closing has had its year, so the first blocking date is the day after.
 *
 * A fixed day count cannot express this — it is 353 or 354 depending on whether a leap day falls
 * inside the window, and rounding up to cover both flags legal closings in the difference.
 *
 * UTC so the rendered question text and any server-side recomputation cannot disagree across a
 * local midnight.
 */
const getTexas50a6ThresholdDate = (applicationDate) => {
    const threshold = new Date(applicationDate.getTime());
    threshold.setUTCDate(threshold.getUTCDate() + APPLICATION_TO_CLOSING_DAYS + 1);
    threshold.setUTCFullYear(threshold.getUTCFullYear() - 1);
    return threshold;
};
exports.getTexas50a6ThresholdDate = getTexas50a6ThresholdDate;
